import { Cell, Grid, HFlow, Icon, Text, useTheme, VFlow } from 'bold-ui'
import {
  AtendimentosPreNatalPuerperioConsolidado,
  ExamesRealizadosPreNatalConsolidado,
  MedicoesPreNatalConsolidado,
  PreNatalPuerperioConsolidado,
  VisitasDomiciliaresPreNatalPuerperioConsolidado,
} from 'graphql/types.generated'
import React from 'react'
import { CidadaoListagemGestantePuerperaQueryModel } from 'view/acompanhamento/acompanhamento-condicao-saude/model/model-acompanhamentoCondicaoSaudeListaTematica'
import { labelByStatus } from 'view/acompanhamento/acompanhamento-condicao-saude/util-acompanhamentoCondicaoSaude'
import { RiscoGravidezTag } from 'view/atendimentos/detail/soap/pre-natal/components/RiscoGravidezTag'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

interface AcompanhamentoCondicaoSaudeGestantePuerperaAccordionSectionProps {
  row: CidadaoListagemGestantePuerperaQueryModel
}

export function AcompanhamentoCondicaoSaudeGestantePuerperaAccordionSection(
  props: Readonly<AcompanhamentoCondicaoSaudeGestantePuerperaAccordionSectionProps>
) {
  const { row } = props
  const theme = useTheme()
  const iconColor = theme.pallete.gray.c20
  const gestantePuerpera = row.cidadao?.cidadaoConsolidado?.preNatalPuerperio

  return (
    <>
      {renderIg(gestantePuerpera, iconColor)}
      {renderPreNatal(gestantePuerpera, iconColor)}
      {renderMedicoes(gestantePuerpera.medicoesPreNatal, iconColor)}
      {renderExames(gestantePuerpera.examesRealizadosPreNatal, iconColor)}
      {renderVisitasDomiciliares(gestantePuerpera.visitasDomiciliares, iconColor)}
      {renderPuerperio(gestantePuerpera.atendimentosPreNatalPuerperio, iconColor)}
    </>
  )
}
const renderIg = (gestantePuerpera: PreNatalPuerperioConsolidado, iconColor: string) => (
  <Grid gap={0} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='infoCircleFilled' color={iconColor} />
        <Text fontWeight='bold'>Informações da gestação</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Risco gestacional</Text>
        <RiscoGravidezTag isAltoRisco={gestantePuerpera?.altoRiscoGestacional} />
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>IG ecográfica</Text>
        <Text>{formatIdadeGestacional(gestantePuerpera?.idadeGestacionalEcograficaEmDias) ?? '-'}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>IG cronológica</Text>
        <Text>{formatIdadeGestacional(gestantePuerpera?.idadeGestacionalCronologicaEmDias) ?? '-'}</Text>
      </VFlow>
    </Cell>
  </Grid>
)

const renderPreNatal = (gestantePuerpera: PreNatalPuerperioConsolidado, iconColor: string) => (
  <Grid gap={0} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='pregnant' color={iconColor} />
        <Text fontWeight='bold'>Pré-natal</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Consultas</Text>
        <Text>{gestantePuerpera?.atendimentosPreNatalPuerperio?.consultasPreNatalGestacao ?? 0}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Consultas até 12 semanas</Text>
        <Text>{gestantePuerpera?.atendimentosPreNatalPuerperio?.consultasPrimeirasDozeSemanas ?? 0}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Consultas odontológicas</Text>
        <Text>{gestantePuerpera?.atendimentosPreNatalPuerperio?.consultasOdontologicasGestacao ?? 0}</Text>
      </VFlow>
    </Cell>
  </Grid>
)
const renderMedicoes = (medicoesPreNatal: MedicoesPreNatalConsolidado, iconColor: string) => (
  <Grid gap={0.5} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='rulerFilled' color={iconColor} />
        <Text fontWeight='bold'>Medições (quantidade)</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Registros de altura uterina</Text>
        <Text>{medicoesPreNatal?.qtdMedicoesAlturaUterina ?? 0}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Registros de pressão arterial</Text>
        <Text>{medicoesPreNatal?.qtdMedicoesPressaoArterial ?? 0}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Registros de peso e altura simultâneos</Text>
        <Text>{medicoesPreNatal?.qtdMedicoesPesoAltura ?? 0}</Text>
      </VFlow>
    </Cell>
  </Grid>
)

const renderExames = (examesRealizadosPreNatal: ExamesRealizadosPreNatalConsolidado, iconColor: string) => (
  <Grid gap={0} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='fileWithItemsFilled' color={iconColor} />
        <Text fontWeight='bold'>Exames - Avaliação</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>HIV (1º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.hivPrimeiroTrimestre)}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Sífilis (1º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.sifilisPrimeiroTrimestre)}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Hepatite B (1º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.hepatiteBPrimeiroTrimestre)}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Hepatite C (1º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.hepatiteCPrimeiroTrimestre)}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>HIV (3º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.hivTerceiroTrimestre)}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Sífilis (3º trimestre)</Text>
        <Text>{labelByStatus(examesRealizadosPreNatal?.sifilisTerceiroTrimestre)}</Text>
      </VFlow>
    </Cell>
  </Grid>
)

const renderVisitasDomiciliares = (
  visitasDomiciliares: VisitasDomiciliaresPreNatalPuerperioConsolidado,
  iconColor: string
) => (
  <Grid gap={0} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='houseFilled' color={iconColor} />
        <Text fontWeight='bold'>Visitas domiciliares</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Pré-natal</Text>
        <Text>{visitasDomiciliares?.visitasDomiciliaresPreNatal ?? 0}</Text>
      </VFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Puerpério</Text>
        <Text>{visitasDomiciliares?.visitasDomiciliaresPuerperio ?? 0}</Text>
      </VFlow>
    </Cell>
  </Grid>
)

const renderPuerperio = (
  atendimentosPreNatalPuerperio: AtendimentosPreNatalPuerperioConsolidado,
  iconColor: string
) => (
  <Grid gap={0} alignItems='baseline'>
    <Cell size={12}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon width='1rem' icon='nurse' color={iconColor} />
        <Text fontWeight='bold'>Puerpério</Text>
      </HFlow>
    </Cell>
    <Cell size={3}>
      <VFlow vSpacing={0.25}>
        <Text fontWeight='bold'>Consultas</Text>
        <Text>{atendimentosPreNatalPuerperio?.consultasPuerperio ?? 0}</Text>
      </VFlow>
    </Cell>
  </Grid>
)
